import { Directive, HostListener, ElementRef, Renderer2, Input } from '@angular/core';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

@Directive({
    selector: '[appPhoneNumberMask]',
    standalone: false
})

export class PhoneNumberMaskDirective {

  @Input() phoneNumberFormat: string = 'default'; // Default format

  // Guards against the re-entrancy caused by re-dispatching the 'input' event below.
  private isProcessing = false;

  constructor(private el: ElementRef<HTMLInputElement>, private renderer: Renderer2) { }

  @HostListener('input', ['$event'])
  onInput(event: Event) {
    if (this.isProcessing) { return; }

    const input = this.el.nativeElement as HTMLInputElement;
    const previousValue = input.value;
    const caret = input.selectionStart ?? previousValue.length;

    // How many digits sit to the left of the caret in the current value. This is
    // the anchor we preserve across reformatting, so the caret stays put when the
    // user edits in the middle instead of jumping to the end.
    const digitsBeforeCaret = previousValue.slice(0, caret).replace(/[^\d]/g, '').length;

    const maskedValue = this.maskPhoneNumber(previousValue);
    const newCaret = this.caretPositionForDigits(maskedValue, digitsBeforeCaret);

    this.isProcessing = true;
    this.renderer.setProperty(input, 'value', maskedValue);
    // Notify Angular's value accessor so the bound FormControl picks up the masked value.
    input.dispatchEvent(new Event('input'));
    this.isProcessing = false;

    // Restore the caret AFTER the value has been written back.
    try {
      input.setSelectionRange(newCaret, newCaret);
    } catch {
      // setSelectionRange is unsupported on some input types — safe to ignore.
    }
  }

  // Returns the index in `masked` that sits immediately after the Nth digit, so the
  // caret can be placed just past the same digit the user was editing.
  private caretPositionForDigits(masked: string, digitCount: number): number {
    if (digitCount <= 0) { return 0; }
    let seen = 0;
    for (let i = 0; i < masked.length; i++) {
      if (/\d/.test(masked[i])) {
        seen++;
        if (seen === digitCount) { return i + 1; }
      }
    }
    return masked.length;
  }

  private maskPhoneNumber(value: string): string {
    // Remove any non-digit characters from the input
    const cleanValue = value.replace(/[^\d]/g, '');

    // Apply the desired format to the phone number based on the specified format
    if (this.phoneNumberFormat === 'tenDigit') {
      if (cleanValue.length >= 10) {
        const areaCode = cleanValue.slice(0, 3);
        const firstPart = cleanValue.slice(3, 6);
        const secondPart = cleanValue.slice(6, 10);

        return `${areaCode}-${firstPart}-${secondPart}`;
      }
    } else if (this.phoneNumberFormat === 'elevenDigit') {
      if (cleanValue.length >= 11) {
        const countryCode = cleanValue.slice(0, 1);
        const areaCode = cleanValue.slice(1, 4);
        const phoneNumber = cleanValue.slice(4, 7);
        const extension = cleanValue.slice(7, 11);

        return `${countryCode}-${areaCode}-${phoneNumber}-${extension}`;
      }
    }

    return cleanValue;
  }
}
   
//export class PhoneNumberMaskDirective {

//  @Input() phoneNumberFormat: string = 'default'; // Default format

//  constructor(private el: ElementRef<HTMLInputElement>, private renderer: Renderer2) { }

//  @HostListener('input', ['$event'])
//  onInput(event: Event) {
//    const input = this.el.nativeElement as HTMLInputElement;
//    const maskedValue = this.maskPhoneNumber(input.value);
//    this.renderer.setProperty(input, 'value', maskedValue);
//    input.dispatchEvent(new Event('input'));
//  }

//  private maskPhoneNumber(value: string): string {
//    // Remove any non-digit characters from the input
//    const cleanValue = value.replace(/[^\d]/g, '');

//    // Get the guide format based on the specified format
//    const guideFormat = this.getGuideFormat();

//    // Format the phone number based on the input length and guide format
//    let formattedValue = '';
//    let index = 0;
//    for (const char of guideFormat) {
//      if (char === 'X' && cleanValue[index]) {
//        formattedValue += cleanValue[index];
//        index++;
//      } else {
//        formattedValue += char;
//      }
//    }

//    return formattedValue;
//  }

//  private getGuideFormat(): string {
//    if (this.phoneNumberFormat === 'tenDigit') {
//      return 'XXX-XXX-XXXX';
//    } else if (this.phoneNumberFormat === 'elevenDigit') {
//      return 'X-XXX-XXX-XXXX';
//    } else {
//      return 'X-XXX-XXX-XXXX';
//    }
//  }
//}


// Custom validator for 10-digit phone number
export function tenDigitPhoneNumberValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value ? control.value.toString().replace(/[^\d]/g, '') : '';
    // Check if the value is empty, return null to make it not mandatory
    if (!value) {
      return null;
    }
    if (value.length !== 10) {
      return { invalidTenDigitPhoneNumber: true };
    }
    if (value[0] === '0' || value[0] === '1') {
      return { invalidTenDigitPhoneNumber: true };
    }
    return null;
  };
}

// Custom validator for 11-digit phone number (using the existing pattern)
export function elevenDigitPhoneNumberValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value ? control.value.toString().replace(/[^\d]/g, '') : '';
    // Check if the value is empty, return null to make it not mandatory
    if (!value) {
      return null;
    }
    if (value.length !== 11) {
      return { invalidElevenDigitPhoneNumber: true };
    }
    if (value[0] !== '1') {
      return { invalidElevenDigitPhoneNumber: true };
    }
    return value.match(/([0-9]( |-)?)?(\(?[0-9]{3}\)?|[0-9]{3})( |-)?([0-9]{3}( |-)?[0-9]{4}|[a-zA-Z0-9]{7})/) ? null : { invalidElevenDigitPhoneNumber: true };
  };
}

